import { useState } from 'react';
import { Upload } from 'lucide-react';
import type { FormFieldDef } from '@hcc/types';
import { ApiError } from '../../../../lib/api';
import { uploadFormImage } from '../../../../lib/upload';
import { Button } from '../../../ui/Button';
import { FieldView } from '../../formfields/FieldView';

interface ContentConfigProps {
  draft: FormFieldDef;
  patch: (p: Partial<FormFieldDef>) => void;
}

const inputClass =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

/**
 * Config for the static content types: paragraph body, rich_text HTML with
 * live preview, and the image picker (upload or pasted URL).
 */
export function ContentConfig({ draft, patch }: ContentConfigProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      patch({ image_key: await uploadFormImage(file) });
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  if (draft.type === 'paragraph') {
    return (
      <label className="block text-sm">
        <span className="font-medium text-fg-muted">Body</span>
        <textarea
          rows={4}
          value={draft.text ?? ''}
          onChange={(e) => patch({ text: e.target.value })}
          className={inputClass}
        />
      </label>
    );
  }

  if (draft.type === 'rich_text') {
    return (
      <>
        <label className="block text-sm">
          <span className="font-medium text-fg-muted">HTML content</span>
          <textarea
            rows={8}
            value={draft.text ?? ''}
            onChange={(e) => patch({ text: e.target.value })}
            className={inputClass + ' font-mono text-xs'}
            placeholder='<p>Instructions… <a href="https://…">link</a></p>'
          />
          <span className="mt-1 block text-xs text-fg-dim">
            Paste HTML. Scripts and event handlers are stripped on display, and every link opens
            in a new tab. Supported: headings, paragraphs, bold / italic, lists, links, and
            tables.
          </span>
        </label>
        <div className="rounded-token-sm border border-line/10 bg-surface-2 p-3">
          <div className="mb-1.5 text-xs font-medium text-fg-muted">Preview</div>
          <FieldView field={draft} value={null} onChange={() => {}} disabled />
        </div>
      </>
    );
  }

  if (draft.type === 'image' || draft.type === 'damage_markup') {
    return (
      <div className="space-y-2 text-sm">
        <span className="font-medium text-fg-muted">
          {draft.type === 'damage_markup' ? 'Background image (required)' : 'Image'}
        </span>
        {draft.type === 'damage_markup' ? (
          <p className="text-xs text-fg-dim">
            The picture people draw on, e.g. the van diagram. Strokes are saved with the answer
            and a flattened copy is stored on submit.
          </p>
        ) : null}
        {draft.image_key ? (
          <img
            src={draft.image_key}
            alt=""
            className="max-h-40 rounded-token-sm border border-line/10"
          />
        ) : (
          <div className="rounded-token-sm border border-dashed border-line-strong/20 bg-surface-2 px-3 py-6 text-center text-xs text-fg-dim">
            No image yet
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-token-sm border border-line-strong/20 bg-surface px-3 py-1.5 hover:bg-surface-2">
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading…' : draft.image_key ? 'Replace image' : 'Upload image'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              disabled={uploading}
              onChange={onPickImage}
            />
          </label>
          {draft.image_key ? (
            <Button variant="ghost" size="sm" onClick={() => patch({ image_key: undefined })}>
              Remove
            </Button>
          ) : null}
        </div>
        {uploadError ? <p className="text-xs text-danger">{uploadError}</p> : null}
        <label className="block">
          <span className="text-xs text-fg-dim">Or paste an image URL</span>
          <input
            value={draft.image_key ?? ''}
            onChange={(e) => patch({ image_key: e.target.value })}
            className={inputClass}
            placeholder="https://…"
          />
        </label>
        <p className="text-xs text-fg-dim">PNG, JPG, WebP or GIF, up to 5MB.</p>
      </div>
    );
  }

  return null;
}
